import { ALL_VALUE } from 'site-modules/shared/constants/incentives/electric-car-incentives';

const maybe = value => {
  const map = fn => maybe(value ? fn(value) : undefined);
  const unpack = () => value;
  const orElse = fallback => maybe(value || fallback);

  return Object.assign(map, { map, unpack, orElse });
};

const checkIsAllValue = value => (value === ALL_VALUE ? undefined : value);

const checkLength = arr => (Array.isArray(arr) && arr.length ? arr : undefined);

const wrapInArray = value => (Array.isArray(value) ? value : [value]);

const wrapIn = value => ({ in: wrapInArray(value) });

const wrapNotIn = value => ({ notIn: wrapInArray(value) });

const wrapNotInOrIn = excludedValue => includedValue => {
  const isExcludedValue = Array.isArray(excludedValue) ? excludedValue.length : excludedValue;
  const isIncludedValue = Array.isArray(includedValue) ? includedValue.length : includedValue;

  if (!isExcludedValue && !isIncludedValue) {
    return undefined;
  }
  return !isIncludedValue && isExcludedValue ? wrapNotIn(excludedValue) : wrapIn(includedValue);
};

/**
 * Creates a higher-order function that transforms a `[min, max]` tuple
 * into a range query object with `gte` (greater than or equal) and `lte` (less than or equal) keys.
 * It filters values based on the provided `checkFn`.
 *
 * @param {(value: any) => boolean} [checkFn=Boolean] - A predicate function to validate values.
 * Defaults to `Boolean`, which filters out falsy values (0, null, undefined, false, "").
 *
 * @returns {(range: [any, any]) => Record<'gte' | 'lte', any> | undefined}
 * A function that accepts a `[gte, lte]` array and returns the query object or `undefined` if no values pass the check.
 */
const wrapGteLte =
  (checkFn = Boolean) =>
  ([gte, lte]) => {
    const entries = [
      ['lte', lte],
      ['gte', gte],
    ].filter(([, value]) => checkFn(value));

    return entries.length ? Object.fromEntries(entries) : undefined;
  };

export { maybe, checkIsAllValue, checkLength, wrapIn, wrapNotIn, wrapNotInOrIn, wrapInArray, wrapGteLte };
